'use client';

import React, { ReactElement, ReactNode, useContext, useEffect, useState, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid2,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  buttonTextIconLarge,
  gray10,
  gray100,
  gray20,
  gray40,
  gray80,
  navy50,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
  yellow50,
  yellow_overlay10,
} from '../../../constants/colors';
import { boldXxl, regularSm, spHeaderTitle } from '../../../constants/location';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import {
  Add,
  ChevronRight,
  Visibility,
  ContentCopy,
  DeleteOutline,
  EditOutlined,
  ErrorOutline,
  ExpandMore,
  Insights,
  MoreVert,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Timestamp } from 'firebase/firestore';
import CustomLoading from '../../uiParts/form/CustomLoading';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import dayjs from 'dayjs';
import { PatternType } from '../../../types/pattern';
import { deletePattern } from '../../../api/firebase/firestore/pattern/delete';
import { deleteMasterMappingsByPatternId } from '../../../api/firebase/firestore/master/mapping/delete';
import { deleteDictionaryMappingsByPatternId } from '../../../api/firebase/firestore/dictionary/mapping/delete';
import {
  deleteSubPattern,
  deleteSubPatternsByPatternId,
} from '../../../api/firebase/firestore/subPattern/delete';
import {
  getSubPatterns,
  resolveSubPatternSampleResultId,
} from '../../../api/firebase/firestore/subPattern/read';
import { createSubPattern } from '../../../api/firebase/firestore/subPattern/create';
import { updateSubPattern } from '../../../api/firebase/firestore/subPattern/update';
import type { SubPatternType } from '../../../types/subPattern';
import {
  subPatternDeleteConfirm,
  subPatternLabel,
  subPatternJsonLabel,
} from '../../../constants/subPattern';
import SubPatternJsonModal from '../../uiParts/modal/SubPatternJsonModal';
import {
  buildSubPatternJson,
  toItemOverrides,
  type SubPatternJsonType,
} from '../../../utils/pattern/subPatternJson';
import { buildSubPatternOverrideCandidates } from '../../../utils/pattern/subPatternCandidates';
import { isOperatorStaff } from '../../../utils/permission';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { getUser } from '../../../api/firebase/firestore/user/read';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import AccountManagementDialog from '../../uiParts/modal/AccountManagementDialog';
import { companyPlan, isProGeminiModel, patternStatus, planLimits } from '../../../constants/enum';
import { useCompanyGroups } from '../../../hooks/useCompanyGroups';
import { useCompanyUsers } from '../../../hooks/useCompanyUsers';
import {
  PATTERN_LIST_COPY_BUTTON,
  PATTERN_LIST_DETAIL_BUTTON,
  PATTERN_LIST_MENU_ACCURACY,
  PATTERN_LIST_MENU_DELETE,
  PATTERN_LIST_MENU_EDIT_HISTORY,
  PATTERN_LIST_MENU_LABEL,
  PATTERN_LIST_MENU_WEBHOOK,
  SHARE_SCOPE_COLUMN_LABEL,
  PATTERN_MODEL_SELECTABLE_TOOLTIP,
} from '../../../constants/labels';
import { getDisplayName } from '../../../utils/displayName';
import { hasAdminPermission } from '../../../utils/permission';
import { useDetailMode } from '../../../hooks/useDetailMode';

import HiStoryIcon from '../../assets/hi-story-icon.png';
import NoAccountIcon from '../../assets/Account circle.svg';
import { usePatternList, clearPatternListCache } from '../../../hooks/usePatternList';
import { updatePatternsDisplayOrder } from '../../../api/firebase/firestore/pattern/update';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PatternWithUserType extends PatternType {
  showName?: string;
  iconUrl?: string;
}

// ドラッグ可能なテーブル行コンポーネント
interface SortablePatternRowProps {
  data: PatternWithUserType;
  onNavigate: (path: string) => void;
  onCopy: (e: React.MouseEvent, patternId: string) => void;
  onDelete: (e: React.MouseEvent, patternId: string, patternName: string) => void;
  isAtLimit: boolean;
  formatDate: (timestamp: Timestamp | null) => string;
  accuracyMenuLabel: string;
  showWebhookMenuItem: boolean;
  groupNameMap: Record<string, string>; // グループID→名（共有範囲バッジ表示用 #2804）
  userNameMap: Record<string, string>; // 担当者UID→名（限定共有バッジ表示用 #2963）
  userGroupsMap: Record<string, string[]>; // 担当者UID→所属group_ids（重複バッジ抑制用 #2963）
  /**
   * この読取パターンに属する子パターンの行（#3314）。
   *
   * 親行と同じ transform をドラッグ中に掛けて一緒に動かすため、
   * その style を引数で渡して**各行に付けてもらう**。
   * 要素を受け取って cloneElement する方式だと、条件分岐を含む
   * フラグメントを渡されたときに中の行まで届かない
   */
  subPatternRows?: (dragStyle: React.CSSProperties) => ReactNode;
  /**
   * 子パターンのJSON一括登録を開く（#3372）。運営スタッフで、かつ
   * 子パターンが0件のときだけ渡す（1件以上あるときは見出しの右横にボタンを出す）。
   * undefined ならメニューに項目を出さない
   */
  onBulkSubPatternJson?: (patternId: string) => void;
}

/**
 * 限定共有バッジ（#2804 Phase2 / 担当者指定 #2963）
 * 共有先グループ名・担当者名のチップを表示。いずれも未設定（企業全体）は「-」。
 */
const ShareScopeBadge: React.FC<{
  data: PatternType;
  groupNameMap: Record<string, string>;
  userNameMap: Record<string, string>;
  userGroupsMap: Record<string, string[]>;
}> = ({ data, groupNameMap, userNameMap, userGroupsMap }) => {
  const groupIds = (data.shared_group_ids ?? []).filter(id => groupNameMap[id]);
  const userIds = (data.shared_user_ids ?? [])
    .filter(id => userNameMap[id])
    // 既に共有グループに所属している担当者は、グループChipでカバー済みのため重複表示しない（#2963）
    .filter(id => !(userGroupsMap[id] ?? []).some(gid => groupIds.includes(gid)));
  if (groupIds.length === 0 && userIds.length === 0) {
    return (
      <Typography component="span" sx={{ color: gray80 }}>
        -
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
      {groupIds.map(id => (
        <Chip key={`g_${id}`} label={groupNameMap[id]} size="small" variant="outlined" />
      ))}
      {userIds.map(id => (
        <Chip key={`u_${id}`} label={userNameMap[id]} size="small" variant="outlined" />
      ))}
    </Box>
  );
};

const SortablePatternRow: React.FC<SortablePatternRowProps> = ({
  data,
  onNavigate,
  onCopy,
  onDelete,
  isAtLimit,
  formatDate,
  accuracyMenuLabel,
  showWebhookMenuItem,
  groupNameMap,
  userNameMap,
  userGroupsMap,
  subPatternRows,
  onBulkSubPatternJson,
}) => {
  const isDeleted = data.status === patternStatus.deleted;
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };
  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: data.patternId,
    disabled: isDeleted,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        onClick={(): void => onNavigate('/pattern/' + data.patternId)}
        sx={{
          ...styles.tableDataRow,
          ...(isDeleted && { opacity: 0.6, backgroundColor: gray10 }),
        }}
      >
        {/* ドラッグハンドル */}
        <TableCell sx={{ ...styles.tableDataText, width: '40px', padding: '0 4px' }}>
          {!isDeleted && (
            <IconButton
              {...attributes}
              {...listeners}
              size="small"
              onClick={(e): void => e.stopPropagation()}
              sx={{ cursor: 'grab' }}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>

        {/* 読取パターン名 */}
        <TableCell sx={styles.tableDataText}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={data.pattern_name}>
              <span style={styles.PatternNameText}>{data.pattern_name}</span>
            </Tooltip>
            {isProGeminiModel(data.gemini_model) && (
              <Tooltip title={PATTERN_MODEL_SELECTABLE_TOOLTIP} arrow>
                <Box
                  sx={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    backgroundColor: navy50,
                    color: white,
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontWeight: 'bold',
                  }}
                >
                  Pro
                </Box>
              </Tooltip>
            )}
            {isDeleted && (
              <Box
                sx={{
                  fontSize: '10px',
                  padding: '2px 6px',
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                削除済み
              </Box>
            )}
          </Box>
        </TableCell>

        {/* 備考 */}
        <TableCell sx={styles.tableDataText}>
          <Tooltip title={data.remarks || ''}>
            <span style={styles.RemarksText}>{data.remarks || '-'}</span>
          </Tooltip>
        </TableCell>

        {/* 読取項目数 */}
        <TableCell sx={styles.tableDataText}>
          <Tooltip title={data.item_count.toString()}>
            <span style={styles.ItemCountText}>{data.item_count}</span>
          </Tooltip>
        </TableCell>

        {/* 更新日 */}
        <TableCell sx={styles.tableDataText}>{formatDate(data.updated_at)}</TableCell>

        {/* 作成者 */}
        <TableCell sx={styles.tableCellAvatar}>
          <Avatar src={data.iconUrl || NoAccountIcon} sx={styles.avatar} />
          <Typography sx={{ lineHeight: '24px' }}>
            <Tooltip title={data.showName}>
              <span style={styles.showUserNameText}>{data.showName}</span>
            </Tooltip>
          </Typography>
        </TableCell>

        {/* 共有範囲（#2804 Phase2） */}
        <TableCell sx={styles.tableDataText} onClick={(e): void => e.stopPropagation()}>
          <ShareScopeBadge
            data={data}
            groupNameMap={groupNameMap}
            userNameMap={userNameMap}
            userGroupsMap={userGroupsMap}
          />
        </TableCell>

        {/* 操作 */}
        <TableCell
          sx={{
            ...styles.tableDataText,
            textAlign: 'right',
            whiteSpace: 'nowrap',
            paddingRight: '16px',
          }}
        >
          <Box sx={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
            <Button
              onClick={(e): void => onCopy(e, data.patternId)}
              startIcon={<ContentCopy />}
              sx={[
                secondaryButtonBackgroundColor,
                styles.buttonHeight,
                {
                  fontSize: '12px',
                  padding: '4px 8px',
                  // 子パターン行の「編集」と縦に並ぶため、文字数が違っても幅を揃える
                  minWidth: '84px',
                  ...(isDeleted && { visibility: 'hidden' }),
                },
              ]}
              disabled={isAtLimit}
            >
              {PATTERN_LIST_COPY_BUTTON}
            </Button>

            <Button
              onClick={(e): void => {
                e.stopPropagation();
                onNavigate(`/ai-ocr/pattern/${data.patternId}`);
              }}
              startIcon={<Visibility />}
              sx={[
                secondaryButtonBackgroundColor,
                styles.buttonHeight,
                {
                  fontSize: '12px',
                  padding: '4px 8px',
                  // 子パターン行の「削除」と縦に並ぶため幅を揃える
                  minWidth: '84px',
                },
              ]}
            >
              {PATTERN_LIST_DETAIL_BUTTON}
            </Button>

            <IconButton
              aria-label={PATTERN_LIST_MENU_LABEL}
              onClick={handleMenuOpen}
              size="small"
              sx={{
                color: gray80,
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={(e): void => e.stopPropagation()}
            >
              <MenuItem
                onClick={(e): void => {
                  e.stopPropagation();
                  setMenuAnchorEl(null);
                  onNavigate(`/pattern/${data.patternId}/accuracy`);
                }}
              >
                <ListItemIcon>
                  <Insights fontSize="small" />
                </ListItemIcon>
                <ListItemText>{accuracyMenuLabel}</ListItemText>
              </MenuItem>
              {showWebhookMenuItem && (
                <MenuItem
                  onClick={(e): void => {
                    e.stopPropagation();
                    setMenuAnchorEl(null);
                    onNavigate(`/ai-ocr/pattern/${data.patternId}/webhook`);
                  }}
                >
                  <ListItemIcon>
                    <WebhookIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{PATTERN_LIST_MENU_WEBHOOK}</ListItemText>
                </MenuItem>
              )}
              {/*
                子パターンのJSON一括登録（#3372）。運営スタッフのみ。
                子パターンが1件以上あるときは見出しの右横にボタンを出すため、
                ここには出さない（0件のときだけの導線）
              */}
              {onBulkSubPatternJson && (
                <MenuItem
                  onClick={(e): void => {
                    e.stopPropagation();
                    setMenuAnchorEl(null);
                    onBulkSubPatternJson(data.patternId);
                  }}
                >
                  <ListItemIcon>
                    <DescriptionIcon fontSize="small" sx={{ color: gray80 }} />
                  </ListItemIcon>
                  <ListItemText>{subPatternJsonLabel.bulkImportMenu}</ListItemText>
                </MenuItem>
              )}
              {!isDeleted && (
                <MenuItem
                  onClick={(e): void => {
                    e.stopPropagation();
                    setMenuAnchorEl(null);
                    onDelete(e, data.patternId, data.pattern_name);
                  }}
                >
                  <ListItemIcon>
                    <DeleteOutline fontSize="small" sx={{ color: gray80 }} />
                  </ListItemIcon>
                  <ListItemText>{PATTERN_LIST_MENU_DELETE}</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </TableCell>
      </TableRow>
      {/*
      子パターンの行（#3314）。親行と同じ transform・transition を渡し、
      ドラッグしたときに親行と一緒に動くようにする。
      ドラッグ中の半透明（opacity）も揃え、1つのまとまりとして見せる
    */}
      {subPatternRows?.(style)}
    </>
  );
};

/**
 * 子パターンの行の字下げ幅（#3314）。
 *
 * 親行のパターン名は展開アイコン（24px + gap 8px）の分だけ右にあるため、
 * それより更に右へ置いて親パターンの配下であることを示す。
 * 見出しと子パターン名の両方で使い、縦に揃える。
 */
const SUB_PATTERN_INDENT = '56px';

const styles = {
  avatar: {
    height: '24px',
    width: '24px',
  },
  buttonLarge: {
    height: '44px',
    padding: '16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  headerButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...spHeaderTitle,
    },
  },
  icon24: {
    height: '24px',
    marginLeft: '0px',
    width: '24px',
  },
  table: {
    minWidth: '960px',
    width: '100%',
  },
  tableCellAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableDataRow: {
    height: '48px',
    '&:hover': {
      backgroundColor: gray20,
      cursor: 'pointer',
    },
  },
  // 子パターンの入れ子行（#2967）。マスタ一覧の紐付けパターン表示と同じ見せ方に揃える
  subPatternRow: {
    backgroundColor: gray10,
  },
  subPatternCell: {
    ...regularSm,
    color: gray100,
    padding: '8px 16px',
  },
  /** 1列目のドラッグハンドル分。親行と同じ幅にして列位置を揃える */
  subPatternIndentCell: {
    padding: '0 4px',
    width: '40px',
  },
  /** 子パターンの見出し行（#3314）。行全体が開閉のクリック領域になる */
  subPatternHeaderRow: {
    backgroundColor: gray20,
    cursor: 'pointer',
    '&:hover': { backgroundColor: gray40 },
  },
  subPatternHeaderInner: {
    alignItems: 'center',
    display: 'flex',
    gap: '4px',
    // アイコンの分だけ左に戻し、文字の開始位置を子パターン名と揃える
    paddingLeft: `calc(${SUB_PATTERN_INDENT} - 28px)`,
  },
  subPatternHeaderText: {
    ...regularSm,
    color: gray80,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  /**
   * 削除済みパターンの区切り見出し（#3372）。
   *
   * 削除済みは末尾へまとめているため、この行から下がすべて削除済みになる。
   * 子パターンの見出し行と同じ体裁にそろえ、一覧の中の区切りとして馴染ませる
   */
  deletedSectionRow: {
    backgroundColor: gray10,
  },
  deletedSectionCell: {
    borderBottom: `1px solid ${gray40}`,
    padding: '6px 16px',
  },
  deletedSectionText: {
    ...regularSm,
    color: gray80,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  /**
   * JSONでの一括登録のボタン（#3372）。運営スタッフのみ。
   *
   * 見出し「子パターン（N件）」の右横に置くため、行の高さを崩さない小さめの寸法にする。
   * 子パターンの主な操作は行ごとの編集・削除なので、ここは補助的な位置づけとして
   * ターシャリ（白背景・グレー枠）にし、枠線も細く文字も小さめにして控えめに見せる
   */
  subPatternJsonButton: {
    height: '22px',
    minWidth: 'auto',
    padding: '0 8px',
    whiteSpace: 'nowrap',
    borderRadius: '4px',
    borderColor: gray40,
    color: gray80,
    '&:hover': {
      backgroundColor: gray10,
      borderColor: gray40,
      color: gray80,
      boxShadow: 'none',
      opacity: 1,
    },
  },
  subPatternJsonLabel: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1,
  },
  /** 行クリックで開閉するため、アイコンは表示のみ */
  subPatternExpandIcon: {
    alignItems: 'center',
    color: gray80,
    display: 'flex',
    justifyContent: 'center',
  },
  /** 親行のコピー・詳細と同じ見た目・同じ幅にする（縦に並ぶため） */
  subPatternActionButton: {
    fontSize: '12px',
    minWidth: '84px',
    padding: '4px 8px',
  },
  /** 親行の3点リーダー（IconButton size="small"）と同じ幅を確保する */
  subPatternMenuSpacer: {
    flexShrink: 0,
    width: '30px',
  },
  subPatternFooterRow: {
    backgroundColor: gray10,
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  PatternNameText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '250px',
    display: 'inline-block',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  RemarksText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '250px',
    display: 'inline-block',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  ItemCountText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: '100px',
    display: 'inline-block',
    verticalAlign: 'middle',
  } as React.CSSProperties,
  showUserNameText: {
    display: 'inline-block',
    verticalAlign: 'middle',
    wordBreak: 'break-word' as const,
  } as React.CSSProperties,
  icon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  title: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    fontWeight: '500',
    color: gray100,
    fontSize: '22px',
    fontStyle: 'normal',
    lineHeight: '20px',
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  buttonHeight: {
    height: '32px',
  },
};

const PatternList = (): ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading, setCurrentCompany } = useContext(AuthContext);
  // 代理ログイン中の操作を記録するため（#3314）。子パターンのJSON一括登録でも使う
  const { operatorId } = useContext(ProxyLoginContext);
  const { isProxyMode } = useCompanyId();
  const { isDetailView } = useDetailMode();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();

  // 編集履歴メニュー: 全ユーザーへ開放（issue #2828）。
  // ラベルは運営スタッフ視点（詳細モードON）のときのみ「精度確認」、
  // それ以外（一般ユーザー、詳細モードOFFで顧客視点）は「編集履歴」を表示する。
  const showStaffView = isDetailView;
  const accuracyMenuLabel = showStaffView
    ? PATTERN_LIST_MENU_ACCURACY
    : PATTERN_LIST_MENU_EDIT_HISTORY;

  // Webhook 設定: 企業の webhook_enabled かつ
  // 企業管理者 or 運営スタッフ（詳細モードON）の場合のみ表示
  const showWebhookMenuItem =
    company?.webhook_enabled === true && (hasAdminPermission(user?.role) || isDetailView);

  // usePatternList フックでデータ取得
  const { patterns, loading: patternsLoading, refresh: refreshPatterns } = usePatternList();

  // 表示用データ（ユーザー情報付き）
  const [showData, setShowData] = useState<PatternWithUserType[]>([]);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);
  // グループバッジ表示用（#2804 Phase2）。共通フックで取得
  const { groupNameMap } = useCompanyGroups(company?.company_id || user?.company_id);
  // 担当者バッジ表示・重複抑制用（#2963）。企業メンバーを共通フックで取得（二重ロード回避）
  const { userNameMap, userGroupsMap } = useCompanyUsers(company?.company_id || user?.company_id);

  // 子パターン（#2967）
  // 機能が有効な企業でのみ展開アイコン・子パターン行を出す
  // 子パターンは全ユーザーが使える。企業ごとの有効化フラグは廃止した（#2967）
  const subPatternEnabled = true;
  // 展開中のパターンID。子パターンは展開したときに初めて読み込む（一覧表示時に
  // パターン数ぶんサブコレクションを引くのを避けるため）
  // 折りたたんだパターンID（#2967）。
  //
  // 「開いたもの」ではなく**閉じたもの**を覚える。子パターンは既定で開いた状態にしたいので、
  // 開いた側を管理すると初期表示のために全パターンIDを入れる必要があり、
  // 一覧の再取得のたびに同期させる手間が生まれるため。
  const [collapsedPatternIds, setCollapsedPatternIds] = useState<Set<string>>(new Set());
  const [variantsByPattern, setSubPatternsByPattern] = useState<Record<string, SubPatternType[]>>(
    {},
  );
  // 子パターンの更新者（#2967）。子パターンは UID しか持たないため、
  // 表示のたびに引かなくて済むよう UID → 表示名・アイコンで覚えておく。
  // パターン行と同じ方式（保存時に非正規化せず、表示時に users から引く）
  const [subPatternUsers, setSubPatternUsers] = useState<
    Record<string, { name: string; iconUrl: string }>
  >({});
  // 編集ボタンから見本の結果を探している最中の子パターンID（#2967）。
  // 結果の検索に数百ms かかることがあるため、押したことが分かるようにする
  const [subPatternNavigating, setSubPatternNavigating] = useState<string | null>(null);

  // 削除機能関連
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [selectedDeletePatternId, setSelectedDeletePatternId] = useState<string>('');
  const [selectedDeletePatternName, setSelectedDeletePatternName] = useState<string>('');
  // 子パターンの削除確認（#2967）
  const [deleteSubPatternTarget, setDeleteSubPatternTarget] = useState<{
    patternId: string;
    variant: SubPatternType;
  } | null>(null);
  // 子パターンのJSON一括登録（#3372）。対象の読取パターンID
  const [bulkJsonPatternId, setBulkJsonPatternId] = useState<string | null>(null);
  const [bulkJsonSubmitting, setBulkJsonSubmitting] = useState(false);
  /**
   * 1件だけJSONで編集する対象（#3372）。
   * 見本になる結果が無く結果画面へ送れない子パターン（一括登録した直後など）で、
   * 「編集」の代わりに開く
   */
  const [singleJsonTarget, setSingleJsonTarget] = useState<{
    patternId: string;
    variant: SubPatternType;
  } | null>(null);
  /**
   * 子パターンのJSON一括登録を使えるか（#3372）。
   * 項目IDを直接扱うため、親パターンの項目リストJSON（#3039）と同じく運営スタッフ限定
   */
  const canUseSubPatternJson = isOperatorStaff(user?.role);

  /**
   * 削除済みパターンを末尾へまとめた一覧（#3372）。
   *
   * 削除済みは運営スタッフの詳細モードでしか出ないが（usePatternList）、
   * グレーアウトされた行が生きているパターンの間に挟まると一覧が読みにくい。
   * display_order は削除してもそのまま残るため、取得順のままだと混在する。
   *
   * 並び替え（display_order の保存）は有効なパターンだけを対象にしており、
   * 削除済みを末尾へ動かしても保存される順序は変わらない。
   * 有効なパターン同士の相対順序は崩さない（安定ソート）。
   */
  const sortedShowData = useMemo(
    () =>
      [...showData].sort((a, b) => {
        const aDeleted = a.status === patternStatus.deleted ? 1 : 0;
        const bDeleted = b.status === patternStatus.deleted ? 1 : 0;
        return aDeleted - bDeleted;
      }),
    [showData],
  );

  /** 削除済みパターンの件数（#3372）。区切りの見出しに出す */
  const deletedPatternCount = useMemo(
    () => showData.filter(pattern => pattern.status === patternStatus.deleted).length,
    [showData],
  );
  // フリープランのアップグレード促進ダイアログ
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState<boolean>(false);

  // D&D センサー
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // 有効な読取パターン数（削除済みは除外）
  // 代理ログイン詳細モードONでは showData に削除済み(status=99)も含まれるため、
  // 上限カウント等には status=valid のパターンのみを使用する
  const activePatternCount = useMemo(
    () => showData.filter(p => p.status === patternStatus.valid).length,
    [showData],
  );

  // 読取パターン上限チェック
  const patternLimitInfo = useMemo(() => {
    if (!company) {
      return { isAtLimit: false, limit: 0, current: 0 };
    }

    const limit = company.pattern_limit ?? planLimits.free.patternLimit;

    // 有効なパターン数を優先して使用（削除後に即座に反映され、削除済みは含めない）
    const current =
      activePatternCount > 0 ? activePatternCount : (company.current_pattern_count ?? 0);
    const isFreePlan = company.plan === companyPlan.free;
    return {
      isAtLimit: current >= limit,
      limit,
      current,
      isFreePlan,
    };
  }, [company, activePatternCount]);

  // patterns が更新されたらユーザー情報を付与
  useEffect(() => {
    if (!user || patternsLoading || isLoading) return;

    const enrichPatterns = async (): Promise<void> => {
      setLoading(true);
      try {
        const updatedResults = await Promise.all(
          patterns.map(async data => {
            const userId = data.created_by;
            const userData = await getUser(userId);
            const iconUrl = data.is_proxy ? HiStoryIcon : userData?.icon_url;
            const showName = getDisplayName(userData?.name, data.is_proxy);
            return { ...data, showName, iconUrl } as PatternWithUserType;
          }),
        );
        setShowData(updatedResults);
        setShowDataFlg(updatedResults.length > 0);
      } catch {
        setShowDataFlg(false);
        setShowData([]);
      } finally {
        setLoading(false);
      }
    };

    enrichPatterns();
  }, [patterns, patternsLoading, isLoading, user]);

  // タイムスタンプ→日付のみ変換
  const formatDate = (resultTimestamp: Timestamp | null): string => {
    if (!resultTimestamp) return '-';
    return dayjs(resultTimestamp.seconds * 1000).format('YYYY/MM/DD');
  };

  // D&D 並び替えハンドラー
  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // インデックスは画面の並び（削除済みを末尾へ寄せたもの）を基準にする。
    // showData の取得順で引くと、削除済みが挟まっている場合にずれる
    const oldIndex = sortedShowData.findIndex(item => item.patternId === active.id);
    const newIndex = sortedShowData.findIndex(item => item.patternId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(sortedShowData, oldIndex, newIndex);
    const previousData = showData;
    setShowData(newOrder);

    try {
      await updatePatternsDisplayOrder(
        newOrder.filter(p => p.status === patternStatus.valid).map(p => p.patternId),
      );
      // キャッシュをクリアして次回取得時に新しい順序を反映
      clearPatternListCache();
      // サイドバーの読取パターン一覧を更新
      window.dispatchEvent(new Event('pattern-list-updated'));
    } catch {
      // 失敗時は元の順序に戻す
      setShowData(previousData);
      setErrorText('並び替えの保存に失敗しました');
      setSnackbarOpen(true);
    }
  };

  /**
   * 画面を開いたら、子パターンを持つパターンの子パターンを読み込む（#2967）。
   *
   * 既定で展開表示するため先に取ってしまう。ただし**子パターンを持つパターンだけ**に絞る。
   * 全パターンぶん引くと、子パターンを使っていない企業でもパターン数と同じ回数の
   * クエリが走る（上限300件）。
   */
  useEffect(() => {
    if (!subPatternEnabled) return;
    showData
      .filter(pattern => {
        if (pattern.status === patternStatus.deleted) return false;
        // sub_pattern_count は後から追加したフィールド。未設定の既存パターンは
        // has_sub_patterns で代替する（展開アイコンの出し分けと同じ判定）
        return pattern.sub_pattern_count !== undefined
          ? pattern.sub_pattern_count > 0
          : pattern.has_sub_patterns === true;
      })
      .forEach(pattern => {
        void loadSubPatterns(pattern.patternId);
      });
    // showData の変化（一覧の取得完了・並べ替え）を契機に読み込む
  }, [showData, subPatternEnabled]);

  /** 指定パターンの子パターンを読み込む（#2967）。取得済みなら何もしない */
  const loadSubPatterns = async (patternId: string): Promise<void> => {
    if (variantsByPattern[patternId]) return;
    try {
      const variants = await getSubPatterns(patternId);
      setSubPatternsByPattern(prev => ({ ...prev, [patternId]: variants }));
      void loadSubPatternUserNames(variants);
    } catch {
      setErrorText('子パターンの取得に失敗しました');
      setSnackbarOpen(true);
    }
  };

  /**
   * 子パターンの展開/折りたたみ（#2967）。
   * 既定は展開なので、ここでは閉じた状態を出し入れする。
   */
  const toggleSubPatternExpand = async (patternId: string): Promise<void> => {
    const willExpand = collapsedPatternIds.has(patternId);
    setCollapsedPatternIds(prev => {
      const next = new Set(prev);
      if (willExpand) next.delete(patternId);
      else next.add(patternId);
      return next;
    });

    if (willExpand) await loadSubPatterns(patternId);
  };

  /**
   * 子パターンの更新者名をまとめて取得する（#2967）。
   *
   * 子パターンは UID しか持たないため、表示名は users から引く。
   * 取得済みの UID は引き直さない（同じ人が複数の子パターンを作るのが普通のため）。
   * 名前が出せなくても一覧は成立するので、失敗しても画面は止めない。
   */
  const loadSubPatternUserNames = async (variants: SubPatternType[]): Promise<void> => {
    const unknownIds = Array.from(
      new Set(variants.map(v => v.updated_by).filter(uid => uid && !subPatternUsers[uid])),
    );
    if (unknownIds.length === 0) return;

    const entries = await Promise.all(
      unknownIds.map(async uid => {
        try {
          const userData = await getUser(uid);
          return [
            uid,
            {
              // 代理かどうかは子パターンごとに異なるため、ここでは素の名前を持つ。
              // 「事務局」への差し替えは表示時に is_proxy を見て行う（#3314）
              name: userData?.name ?? '',
              iconUrl: userData?.icon_url ?? '',
            },
          ] as const;
        } catch {
          return [uid, { name: '', iconUrl: '' }] as const;
        }
      }),
    );
    setSubPatternUsers(prev => ({ ...prev, ...Object.fromEntries(entries) }));
  };

  /**
   * 子パターンの編集モーダルを開く（#2967）。
   * 上書きできる項目の候補は親パターンの item_list から組み立てる。
   * 一覧のデータに item_list が含まれるため、ここで取り直す必要はない。
   */
  const openSubPatternEditModal = async (
    patternId: string,
    variant: SubPatternType,
  ): Promise<void> => {
    const pattern = showData.find(p => p.patternId === patternId);
    if (!pattern) return;

    // 取得ルールは「帳票のどこから取るか」を書くものなので、**帳票を見ないと編集できない**。
    // そのため一覧では編集させず、見本になる結果の画面へ送る（#2967）。
    // 作成のきっかけになった結果を優先し、消えていれば同じ子パターンの最新の結果を使う
    setSubPatternNavigating(variant.subPatternId);
    try {
      const sampleResultId = await resolveSubPatternSampleResultId(
        patternId,
        variant.subPatternId,
        variant.source_result_id,
      );
      if (!sampleResultId) {
        // 見本になる結果が無い場合（#3372）。
        // JSONで一括登録した直後は source_result_id が無く、その子パターンで
        // 読み取った結果もまだ無いため、ここに来る。
        // 読み取り自体は動くが編集導線だけが開かない状態なので、
        // 運営スタッフにはJSONでの編集を代わりに出す（帳票が無くても内容は直せる）。
        if (canUseSubPatternJson) {
          setSingleJsonTarget({ patternId, variant });
          return;
        }
        setErrorText(
          'この子パターンで読み取った結果が見つかりません。同じ様式の帳票を読み取ってから編集してください。',
        );
        setSnackbarOpen(true);
        return;
      }
      // 遷移先で編集モーダルを開く（#2967）。一覧の「編集」から来たことを伝える
      navigate(`/ai-ocr/pattern/${patternId}/result/${sampleResultId}`, {
        state: { editSubPatternId: variant.subPatternId },
      });
    } catch {
      setErrorText('編集する結果の取得に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setSubPatternNavigating(null);
    }
  };

  /**
   * 1件だけJSONで編集して保存する（#3372）。
   *
   * 結果画面の編集モーダルと違い、ここでは帳票を見ずに直すため
   * フォームを経由せずそのまま保存する。
   * 対象は開いている子パターン1件なので、名称で突き合わせず subPatternId で更新する。
   */
  const handleSingleJsonApply = async (items: SubPatternJsonType[]): Promise<void> => {
    const target = singleJsonTarget;
    const item = items[0];
    if (!target || !item || !user?.uid) return;

    setBulkJsonSubmitting(true);
    try {
      await updateSubPattern({
        patternId: target.patternId,
        subPatternId: target.variant.subPatternId,
        subPatternName: item.sub_pattern_name,
        description: item.description,
        itemOverrides: toItemOverrides(item.item_overrides),
        isActive: item.is_active,
        userId: user.uid,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });

      setSingleJsonTarget(null);
      setInfoText('子パターンを更新しました');
      setSnackbarOpen(true);

      try {
        const refreshed = await getSubPatterns(target.patternId);
        setSubPatternsByPattern(prev => ({ ...prev, [target.patternId]: refreshed }));
        void loadSubPatternUserNames(refreshed);
      } catch {
        // 一覧の表示が古いままになるだけで、更新自体は成立している
      }
      refreshPatterns();
    } catch {
      setSingleJsonTarget(null);
      setErrorText('子パターンの更新に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setBulkJsonSubmitting(false);
    }
  };

  // 子パターンの削除（#2967）
  const handleDeleteVariant = async (): Promise<void> => {
    if (!deleteSubPatternTarget) return;
    const { patternId, variant } = deleteSubPatternTarget;

    try {
      await deleteSubPattern(patternId, variant.subPatternId);
      setSubPatternsByPattern(prev => ({
        ...prev,
        [patternId]: (prev[patternId] ?? []).filter(v => v.subPatternId !== variant.subPatternId),
      }));
      setDeleteSubPatternTarget(null);
      setInfoText('子パターンを削除しました');
      setSnackbarOpen(true);
      // has_sub_patterns が変わりうるため一覧を取り直す（展開アイコンの表示に影響する）
      refreshPatterns();
    } catch {
      setDeleteSubPatternTarget(null);
      setErrorText('子パターンの削除に失敗しました');
      setSnackbarOpen(true);
    }
  };

  /**
   * JSON一括登録のモーダルを開く（#3372）。
   *
   * 開く前に既存の子パターンを読み込む。モーダル側の名称重複チェックが
   * これを使うため、未取得のまま開くと「既存にあるのに重複と判定されない」ことがある
   */
  const openBulkJsonModal = async (patternId: string): Promise<void> => {
    await loadSubPatterns(patternId);
    setBulkJsonPatternId(patternId);
  };

  /**
   * 子パターンのJSON一括登録（#3372）。
   *
   * 名称で既存と突き合わせ、同名は更新・無ければ新規作成する。
   * JSONに無い既存の子パターンは消さない（削除は取り消せないため、
   * 一括取り込みは「追加・更新の手段」と位置づける）。
   *
   * writeBatch は使わず1件ずつ順に書く。createSubPattern / updateSubPattern が
   * 末尾で syncHasSubPatterns（親の集約フィールド更新）を呼ぶため、この流れを保つ。
   * 検証は modal 側で全件通してあるので途中失敗は起きにくいが、
   * 失敗しても巻き戻さず「何件目まで登録したか」を伝える。
   * 同じJSONを再度取り込めば、登録済みは名称一致で更新に回る。
   */
  const handleBulkJsonApply = async (items: SubPatternJsonType[]): Promise<void> => {
    const patternId = bulkJsonPatternId;
    if (!patternId || !user?.uid) return;

    // 既存の子パターンは**ここで取り直す**。
    // 一覧の読み込みは「子パターンを持つパターン」だけが対象なので、
    // キャッシュに無い＝0件とは限らない。古いまま名称を突き合わせると、
    // 更新すべき子パターンを新規作成して同名の2件を作ってしまう
    let existing: SubPatternType[];
    try {
      existing = await getSubPatterns(patternId);
    } catch {
      setErrorText('既存の子パターンの取得に失敗しました');
      setSnackbarOpen(true);
      return;
    }
    const byName = new Map(existing.map(sub => [(sub.sub_pattern_name ?? '').trim(), sub]));

    setBulkJsonSubmitting(true);
    let succeeded = 0;
    try {
      for (const item of items) {
        const itemOverrides = toItemOverrides(item.item_overrides);
        const matched = byName.get(item.sub_pattern_name);
        if (matched) {
          await updateSubPattern({
            patternId,
            subPatternId: matched.subPatternId,
            subPatternName: item.sub_pattern_name,
            description: item.description,
            itemOverrides,
            isActive: item.is_active,
            userId: user.uid,
            isProxy: isProxyMode,
            operatorId: isProxyMode ? operatorId || undefined : undefined,
          });
        } else {
          await createSubPattern({
            patternId,
            subPatternName: item.sub_pattern_name,
            description: item.description,
            itemOverrides,
            // JSON取り込みでは見本の結果が決まらないため持たせない。
            // 編集時の見本結果は results から引くフォールバックで解決される
            sourceResultId: null,
            userId: user.uid,
            isProxy: isProxyMode,
            operatorId: isProxyMode ? operatorId || undefined : undefined,
          });
        }
        succeeded += 1;
      }

      setBulkJsonPatternId(null);
      setInfoText(subPatternJsonLabel.bulkSucceeded(succeeded));
      setSnackbarOpen(true);
    } catch {
      setBulkJsonPatternId(null);
      setErrorText(
        succeeded === 0
          ? '子パターンの登録に失敗しました'
          : subPatternJsonLabel.bulkPartiallyFailed(succeeded, items.length),
      );
      setSnackbarOpen(true);
    } finally {
      setBulkJsonSubmitting(false);
      // 登録した分を一覧へ反映する。has_sub_patterns / sub_pattern_count も変わる
      if (succeeded > 0) {
        // loadSubPatterns は取得済みなら何もしないため、ここでは直接引き直す
        // （state の更新は非同期で、キャッシュを消してから呼んでも間に合わない）
        try {
          const refreshed = await getSubPatterns(patternId);
          setSubPatternsByPattern(prev => ({ ...prev, [patternId]: refreshed }));
          void loadSubPatternUserNames(refreshed);
        } catch {
          // 一覧の表示が古いままになるだけで、登録自体は成立している
        }
        // has_sub_patterns / sub_pattern_count が変わるため一覧も取り直す
        refreshPatterns();
      }
    }
  };

  // 読取パターン削除処理
  const handleDeletePattern = async (): Promise<void> => {
    // 二重実行防止
    if (loading) return;

    try {
      setLoading(true);
      await deletePattern(selectedDeletePatternId);

      // パターンに紐づくマスタ照合マッピングも削除する（#3056）。
      // Firestore の master_mappings と Cloud SQL の master_field_mappings を物理削除する。
      const companyId = company?.company_id || user?.company_id;
      if (companyId) {
        await deleteMasterMappingsByPatternId(companyId, selectedDeletePatternId);
        // 辞書変換マッピングも同様に外す（#3344）。
        // マスタ側は物理削除だが、辞書マッピングは論理削除（status=99）が辞書側の流儀。
        //
        // ここで throw するとパターン削除処理が止まるが、パターン本体とマスタ照合は
        // 既に削除済みで巻き戻せない。辞書マッピングは参照されないまま残るだけなので、
        // 失敗しても削除処理は続行し、記録のみ行う。
        try {
          await deleteDictionaryMappingsByPatternId(companyId, selectedDeletePatternId);
        } catch (dictionaryError) {
          Sentry.captureException(dictionaryError);
        }
      }

      // パターンに紐づく子パターンも削除する（#2967）。
      // パターン本体は論理削除だが、子パターンは参照されなくなるため残さない。
      await deleteSubPatternsByPatternId(selectedDeletePatternId);

      // AuthContextのcompanyを即座に更新
      if (company) {
        setCurrentCompany({
          ...company,
          current_pattern_count: Math.max((company.current_pattern_count ?? 0) - 1, 0),
        });
      }

      // 削除された読取パターンをリストから除外
      const updatedData = showData.filter(pattern => pattern.patternId !== selectedDeletePatternId);
      setShowData(updatedData);
      if (updatedData.length === 0) {
        setShowDataFlg(false);
      }

      // サイドバーの読取パターン一覧を更新
      window.dispatchEvent(new Event('pattern-list-updated'));

      // キャッシュをクリアしてリフレッシュ
      clearPatternListCache();
      await refreshPatterns();

      // 成功メッセージを表示
      setInfoText('読取パターンを削除しました');
      setSnackbarOpen(true);
    } catch {
      setErrorText('読取パターンの削除に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setSelectedDeletePatternId('');
      setSelectedDeletePatternName('');
    }
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: '84px',
            },
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>読取パターン一覧</Typography>
          {/* 中央：警告メッセージ（PC時のみ表示） */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              '@media screen and (max-width:960px)': {
                display: 'none',
              },
            }}
          >
            {patternLimitInfo.isAtLimit && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  marginX: '16px',
                  backgroundColor: yellow_overlay10,
                  borderRadius: '4px',
                }}
              >
                <ErrorOutline sx={{ color: yellow50, fontSize: '20px', flexShrink: 0 }} />
                <Typography
                  sx={{
                    ...regularSm,
                    color: yellow50,
                    '@media screen and (max-width:1234px) and (min-width:958px)': {
                      fontSize: '11px',
                    },
                  }}
                >
                  {patternLimitInfo.isFreePlan
                    ? `読取パターン数が上限（${patternLimitInfo.limit}件）に達しています。新規作成するには、プランをアップグレードするか、既存のパターンを削除してください。`
                    : `読取パターン数が上限（${patternLimitInfo.limit}件）に達しています。新規作成するには既存のパターンを削除してください。`}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={styles.headerButtonWrapper}>
            <Button
              onClick={(): void => navigate('/pattern/add')}
              startIcon={<Add sx={styles.icon24} />}
              sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
              disabled={patternLimitInfo.isAtLimit}
            >
              <Typography sx={buttonTextIconLarge}>読取パターン新規作成</Typography>
            </Button>
          </Box>
        </Box>
        {/* 警告メッセージ（SP時のみヘッダー下に表示） */}
        {patternLimitInfo.isAtLimit && (
          <Box
            sx={{
              display: 'none',
              '@media screen and (max-width:960px)': {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px 5px',
                margin: '0 16px',
                backgroundColor: yellow_overlay10,
                borderRadius: '4px',
              },
            }}
          >
            <ErrorOutline sx={{ color: yellow50, fontSize: '20px', flexShrink: 0 }} />
            <Typography
              sx={{ ...regularSm, color: yellow50, lineHeight: '20px', textAlign: 'left' }}
            >
              {patternLimitInfo.isFreePlan
                ? `読取パターン数が上限（${patternLimitInfo.limit}件）に達しています。新規作成するには、プランをアップグレードするか、既存のパターンを削除してください。`
                : `読取パターン数が上限（${patternLimitInfo.limit}件）に達しています。新規作成するには既存のパターンを削除してください。`}
            </Typography>
          </Box>
        )}
        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={styles.tableSection}>
            {showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : showDataFlg ? (
              <Box sx={styles.tableWrapper}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedShowData
                      .filter(d => d.status === patternStatus.valid)
                      .map(d => d.patternId)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Table sx={styles.table}>
                      <TableHead>
                        <TableRow sx={styles.tableHeadRow}>
                          <TableCell
                            sx={{ ...styles.tableHeadText, width: '40px', padding: '0 4px' }}
                          ></TableCell>
                          <TableCell sx={styles.tableHeadText}>読取パターン名</TableCell>
                          <TableCell sx={styles.tableHeadText}>備考</TableCell>
                          <TableCell sx={styles.tableHeadText}>読取項目数</TableCell>
                          <TableCell sx={styles.tableHeadText}>更新日</TableCell>
                          <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                          <TableCell sx={styles.tableHeadText}>
                            {SHARE_SCOPE_COLUMN_LABEL}
                          </TableCell>
                          <TableCell sx={styles.tableHeadText}></TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {sortedShowData.map((data, rowIndex) => {
                          // 子パターン（#2967）。削除済みパターンでは子パターンを扱わない
                          const isDeleted = data.status === patternStatus.deleted;
                          /**
                           * 削除済みの先頭行か（#3372）。
                           * 削除済みは末尾へまとめているため、ここから下がすべて削除済みになる。
                           * 境界に見出しを出して、生きているパターンとの区切りを示す
                           */
                          const isFirstDeleted =
                            isDeleted &&
                            sortedShowData[rowIndex - 1]?.status !== patternStatus.deleted;
                          // 子パターンを1件も持たないパターンでは展開アイコンを出さない（#2967）。
                          // 開いても空なだけで、無駄なサブコレクション取得が発生するため。
                          //
                          // 件数は無効な子パターンも含む（無効だけを持つパターンも編集できる必要がある）。
                          // sub_pattern_count は後から追加したフィールドで、次に子パターンを
                          // 作成・更新・削除するまで既存パターンには入らない。未設定のときは
                          // has_sub_patterns で代替する（有効な子パターンがあることは分かるため）。
                          // 「無効な子パターンだけを持つ」パターンだけは両方とも手がかりが無く
                          // アイコンが出ないが、子パターンを1件でも操作すれば件数が入り復帰する
                          const hasVariants =
                            data.sub_pattern_count !== undefined
                              ? data.sub_pattern_count > 0
                              : data.has_sub_patterns === true;
                          const showSubPatternToggle =
                            subPatternEnabled && !isDeleted && hasVariants;
                          /**
                           * JSONでの一括登録の導線を出すか（#3372）。
                           *
                           * 子パターンが0件のパターンにも出す。「これらの帳票の子パターンを
                           * 作って」という使い方は0件から始まるため、hasVariants で
                           * 隠すと本来いちばん使いたい場面で導線が無くなる
                           */
                          const showSubPatternJsonRow =
                            canUseSubPatternJson && subPatternEnabled && !isDeleted;
                          const expanded = !collapsedPatternIds.has(data.patternId);
                          const variants = variantsByPattern[data.patternId];

                          return (
                            <React.Fragment key={data.patternId}>
                              {isFirstDeleted && (
                                <TableRow sx={styles.deletedSectionRow}>
                                  <TableCell colSpan={8} sx={styles.deletedSectionCell}>
                                    <Typography sx={styles.deletedSectionText}>
                                      削除済み（{deletedPatternCount}件）
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                              <SortablePatternRow
                                data={data}
                                onNavigate={navigate}
                                onCopy={(e, patternId): void => {
                                  e.stopPropagation();
                                  navigate(`/pattern/add?copy=${patternId}`);
                                }}
                                onDelete={(e, patternId, patternName): void => {
                                  e.stopPropagation();
                                  setSelectedDeletePatternId(patternId);
                                  setSelectedDeletePatternName(patternName);
                                  // フリープランかつ有効なパターンが1件のみの場合はアップグレード促進モーダルを先に表示
                                  if (
                                    company?.plan === companyPlan.free &&
                                    activePatternCount === 1
                                  ) {
                                    setUpgradeDialogOpen(true);
                                  } else {
                                    setDeleteModalOpen(true);
                                  }
                                }}
                                isAtLimit={patternLimitInfo.isAtLimit}
                                formatDate={formatDate}
                                accuracyMenuLabel={accuracyMenuLabel}
                                showWebhookMenuItem={showWebhookMenuItem}
                                groupNameMap={groupNameMap}
                                userNameMap={userNameMap}
                                userGroupsMap={userGroupsMap}
                                // 子パターンが0件のときは行自体を出さず、
                                // 右端の3点メニューから登録できるようにする（#3372）
                                onBulkSubPatternJson={
                                  showSubPatternJsonRow && !hasVariants
                                    ? (patternId): void => {
                                        void openBulkJsonModal(patternId);
                                      }
                                    : undefined
                                }
                                subPatternRows={(dragStyle): ReactNode =>
                                  !showSubPatternToggle ? null : (
                                    <>
                                      {variants === undefined ? (
                                        <TableRow style={dragStyle} sx={styles.subPatternRow}>
                                          <TableCell></TableCell>
                                          <TableCell colSpan={7} sx={styles.subPatternCell}>
                                            <Typography sx={{ ...regularSm, color: gray80 }}>
                                              読み込み中...
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        <>
                                          {/*
                                        子パターンの見出し（#3314）。開閉の起点でもあるため、
                                        閉じていても常に出す（見出しごと消えると開く手段が無くなる）。
                                        行全体をクリック領域にし、アイコンは見た目だけにする
                                      */}
                                          {variants.length > 0 && (
                                            <TableRow
                                              style={dragStyle}
                                              sx={styles.subPatternHeaderRow}
                                              onClick={(): void => {
                                                void toggleSubPatternExpand(data.patternId);
                                              }}
                                            >
                                              <TableCell
                                                sx={styles.subPatternIndentCell}
                                              ></TableCell>
                                              <TableCell colSpan={7} sx={styles.subPatternCell}>
                                                <Box sx={styles.subPatternHeaderInner}>
                                                  <Box sx={styles.subPatternExpandIcon}>
                                                    {expanded ? (
                                                      <ExpandMore fontSize="small" />
                                                    ) : (
                                                      <ChevronRight fontSize="small" />
                                                    )}
                                                  </Box>
                                                  <Typography sx={styles.subPatternHeaderText}>
                                                    {subPatternLabel.listSectionHeading}（
                                                    {variants.length}件）
                                                  </Typography>
                                                  {/*
                                                    JSONでの一括登録（#3372）。運営スタッフのみ。
                                                    行全体が開閉のクリック領域なので、
                                                    ここだけは伝播を止める
                                                  */}
                                                  {canUseSubPatternJson && (
                                                    <Button
                                                      sx={[
                                                        tertiaryButtonBackgroundColor,
                                                        styles.subPatternJsonButton,
                                                      ]}
                                                      onClick={(e): void => {
                                                        e.stopPropagation();
                                                        void openBulkJsonModal(data.patternId);
                                                      }}
                                                    >
                                                      <Typography sx={styles.subPatternJsonLabel}>
                                                        {subPatternJsonLabel.bulkImport}
                                                      </Typography>
                                                    </Button>
                                                  )}
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          )}
                                          {expanded &&
                                            variants.map(variant => (
                                              <TableRow
                                                key={variant.subPatternId}
                                                style={dragStyle}
                                                sx={styles.subPatternRow}
                                              >
                                                {/* 1列目：ドラッグハンドル分の空白。親行と列位置を揃える */}
                                                <TableCell
                                                  sx={styles.subPatternIndentCell}
                                                ></TableCell>
                                                {/*
                                          2列目：子パターン名。親パターンの配下であることが
                                          分かるよう、親のパターン名より右へ字下げする（#3314）
                                        */}
                                                <TableCell sx={styles.tableDataText}>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '8px',
                                                      paddingLeft: SUB_PATTERN_INDENT,
                                                    }}
                                                  >
                                                    {/*
                                              親行のパターン名と同じく素の span を使う。
                                              Typography は独自の font-size を持つため、
                                              セル（tableDataText）の指定が上書きされてしまう
                                            */}
                                                    <Tooltip title={variant.sub_pattern_name}>
                                                      <span style={styles.PatternNameText}>
                                                        {variant.sub_pattern_name}
                                                      </span>
                                                    </Tooltip>
                                                    {!variant.is_active && (
                                                      <Chip
                                                        label="無効"
                                                        size="small"
                                                        variant="outlined"
                                                      />
                                                    )}
                                                  </Box>
                                                </TableCell>
                                                {/* 備考・読取項目数の列は差分では使わない */}
                                                <TableCell
                                                  colSpan={2}
                                                  sx={styles.tableDataText}
                                                ></TableCell>
                                                {/* 更新日。親行と同じ文字指定にする */}
                                                <TableCell sx={styles.tableDataText}>
                                                  {formatDate(variant.updated_at ?? null)}
                                                </TableCell>
                                                {/*
                                            親行の作成者セルと同じ構造・同じ文字指定にする。
                                            代理ログイン中の操作は運営スタッフのUIDが入るため、
                                            親行と同じく「事務局」として見せる（#3314）
                                          */}
                                                <TableCell sx={styles.tableCellAvatar}>
                                                  <Avatar
                                                    src={
                                                      variant.is_proxy
                                                        ? HiStoryIcon
                                                        : subPatternUsers[variant.updated_by]
                                                            ?.iconUrl || NoAccountIcon
                                                    }
                                                    sx={styles.avatar}
                                                  />
                                                  <Typography sx={{ lineHeight: '24px' }}>
                                                    <Tooltip
                                                      title={getDisplayName(
                                                        subPatternUsers[variant.updated_by]?.name,
                                                        variant.is_proxy,
                                                      )}
                                                    >
                                                      <span style={styles.showUserNameText}>
                                                        {getDisplayName(
                                                          subPatternUsers[variant.updated_by]?.name,
                                                          variant.is_proxy,
                                                        )}
                                                      </span>
                                                    </Tooltip>
                                                  </Typography>
                                                </TableCell>
                                                <TableCell sx={styles.tableDataText}></TableCell>
                                                {/* 操作ボタン。親行のコピー・詳細と同じ右端に揃える */}
                                                <TableCell
                                                  sx={{
                                                    ...styles.tableDataText,
                                                    textAlign: 'right',
                                                    whiteSpace: 'nowrap',
                                                    paddingRight: '16px',
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: 'inline-flex',
                                                      gap: '8px',
                                                      alignItems: 'center',
                                                    }}
                                                  >
                                                    {/* 親行の「コピー」と同じ列に並べる */}
                                                    <Button
                                                      startIcon={<EditOutlined />}
                                                      sx={[
                                                        secondaryButtonBackgroundColor,
                                                        styles.buttonHeight,
                                                        styles.subPatternActionButton,
                                                      ]}
                                                      disabled={
                                                        subPatternNavigating ===
                                                        variant.subPatternId
                                                      }
                                                      onClick={(): void => {
                                                        void openSubPatternEditModal(
                                                          data.patternId,
                                                          variant,
                                                        );
                                                      }}
                                                    >
                                                      {subPatternLabel.edit}
                                                    </Button>
                                                    {/* 親行の「詳細」と同じ列に並べる */}
                                                    <Button
                                                      startIcon={<DeleteOutline />}
                                                      sx={[
                                                        secondaryButtonBackgroundColor,
                                                        styles.buttonHeight,
                                                        styles.subPatternActionButton,
                                                      ]}
                                                      onClick={(): void =>
                                                        setDeleteSubPatternTarget({
                                                          patternId: data.patternId,
                                                          variant,
                                                        })
                                                      }
                                                    >
                                                      {subPatternLabel.delete}
                                                    </Button>
                                                    {/*
                                              親行の3点リーダー分の空白。子パターンにメニューは無いが、
                                              確保しないと編集・削除がコピー・詳細より右にずれる
                                            */}
                                                    <Box sx={styles.subPatternMenuSpacer} />
                                                  </Box>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                        </>
                                      )}

                                      {variants !== undefined && variants.length === 0 && (
                                        <TableRow style={dragStyle} sx={styles.subPatternRow}>
                                          <TableCell></TableCell>
                                          <TableCell colSpan={7} sx={styles.subPatternCell}>
                                            <Typography sx={{ ...regularSm, color: gray80 }}>
                                              子パターンはまだ登録されていません。読取結果の画面から作成できます。
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </>
                                  )
                                }
                              />
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </SortableContext>
                </DndContext>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DescriptionIcon sx={styles.icon} />
                <Typography sx={styles.title}>読取パターンがありません</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '14px', color: gray80 }}>
                    右上の「読取パターン新規作成」ボタンから読取パターンを作成してください。
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid2>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeletePattern}
        title={`読取パターン「${selectedDeletePatternName}」`}
        description="削除すると元に戻すことはできません。また、これまでに読み取ったOCR結果も閲覧できなくなります。よろしいですか？"
      />

      {/* 子パターンの削除確認（#2967） */}
      <DeleteModal
        open={deleteSubPatternTarget !== null}
        onClose={(): void => setDeleteSubPatternTarget(null)}
        onDelete={handleDeleteVariant}
        title={`${subPatternLabel.feature}「${deleteSubPatternTarget?.variant.sub_pattern_name ?? ''}」`}
        description={subPatternDeleteConfirm.body}
      />

      {/*
        子パターンのJSON一括登録（#3372）。
        検証に使う上書き候補は、一覧のデータに含まれる item_list から組み立てる
        （子パターン編集の候補と同じ関数を使い、画面と判定を食い違わせない）
      */}
      {bulkJsonPatternId !== null && (
        <SubPatternJsonModal
          open
          isBulk
          candidates={buildSubPatternOverrideCandidates(
            showData.find(p => p.patternId === bulkJsonPatternId)?.item_list ?? [],
          )}
          itemList={showData.find(p => p.patternId === bulkJsonPatternId)?.item_list ?? []}
          existingSubPatterns={variantsByPattern[bulkJsonPatternId] ?? []}
          submitting={bulkJsonSubmitting}
          onClose={(): void => setBulkJsonPatternId(null)}
          onApply={(summary): void => {
            void handleBulkJsonApply(summary.items);
          }}
        />
      )}

      {/*
        1件だけJSONで編集する（#3372）。
        見本になる結果が無く結果画面へ送れない子パターン（一括登録した直後など）で、
        「編集」の代わりに開く。帳票を見ずに直すため、そのまま保存する
      */}
      {singleJsonTarget !== null && (
        <SubPatternJsonModal
          open
          isBulk={false}
          candidates={buildSubPatternOverrideCandidates(
            showData.find(p => p.patternId === singleJsonTarget.patternId)?.item_list ?? [],
          )}
          itemList={showData.find(p => p.patternId === singleJsonTarget.patternId)?.item_list ?? []}
          existingSubPatterns={variantsByPattern[singleJsonTarget.patternId] ?? []}
          editingSubPatternId={singleJsonTarget.variant.subPatternId}
          initialJson={buildSubPatternJson({
            subPatternName: singleJsonTarget.variant.sub_pattern_name,
            description: singleJsonTarget.variant.description,
            isActive: singleJsonTarget.variant.is_active !== false,
            // getSubPatterns が読み出し時に camelCase へ変換済み
            itemOverrides: singleJsonTarget.variant.item_overrides,
          })}
          submitting={bulkJsonSubmitting}
          onClose={(): void => setSingleJsonTarget(null)}
          onApply={(summary): void => {
            void handleSingleJsonApply(summary.items);
          }}
        />
      )}

      <AccountManagementDialog
        open={upgradeDialogOpen}
        onClose={(): void => setUpgradeDialogOpen(false)}
        onContact={(): void => {
          setUpgradeDialogOpen(false);
          navigate('/contact?category=plan&type=plan_change');
        }}
        title="プランのアップグレードをご検討ください"
        descriptionLines={[
          'フリープランでは読取パターンを1件のみ作成できます。複数のパターンを管理したい場合は、スタンダードプラン以上へのアップグレードをご検討ください。',
        ]}
        contactButtonText="お問い合わせ"
        onSecondaryAction={(): void => {
          setUpgradeDialogOpen(false);
          setDeleteModalOpen(true);
        }}
        secondaryActionText="それでも削除"
      />

      <CustomLoading loading={loading} />
    </Box>
  );
};

export default PatternList;
